/**
 * Financeiro > Correlações de Importação.
 *
 * Regras de auto-categorização compartilhadas por DOIS fluxos — extrato
 * bancário (conciliação Itaú via API + importação CSV) e fatura de cartão de
 * crédito (upload) — por isso a tela tem menu próprio, fora da Importação de
 * Extrato. O campo "Aplica-se a" delimita o escopo de cada regra; "Lançar
 * automaticamente" é semântica exclusiva do extrato (a fatura de cartão só
 * recebe sugestão, confirmada pelo usuário).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link2, Plus, Edit2, Trash2, RefreshCw } from 'lucide-react';
import { PermissionGuard } from '@components/PermissionGuard';
import { companyService } from '@services/companyService';
import { financialService } from '@services/financialService';
import type { Company } from '@/types/company';
import { formatCnpj } from '@/types/company';
import type { ImportCorrelation } from '@/types/financial';
import { buildCategoryOptions, filterCategoryGroupsByMovement } from './financialUtils';
import type { CategoryOptionGroup } from './financialUtils';
import {
  LoadingSpinner,
  ErrorBox,
  SuccessBox,
  PermissionFallback,
  ModalShell,
  CategorySelect,
  selectCls,
  inputCls,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
} from './financialShared';

// Escopo da correlação — mesmos valores do backend (CorrelationApplies*).
const APPLIES_BOTH = 0;
const APPLIES_STATEMENT = 1;
const APPLIES_CARD = 2;

const appliesToLabel = (appliesTo: number): string => {
  if (appliesTo === APPLIES_STATEMENT) return 'Extrato';
  if (appliesTo === APPLIES_CARD) return 'Cartão de crédito';
  return 'Ambos';
};

const movementLabel = (movement: number | null): string => {
  if (movement === 0) return 'Saída';
  if (movement === 1) return 'Entrada';
  return 'Ambos';
};

// ─── Modal CRUD de correlação ───────────────────────────────────

const CorrelationModal: React.FC<{
  correlation: ImportCorrelation | null; // null = nova
  categoryGroups: CategoryOptionGroup[];
  onSave: (
    description: string,
    accountingPlanErpId: number | null,
    movement: number | null,
    autoCreate: boolean,
    appliesTo: number,
  ) => Promise<void>;
  onClose: () => void;
}> = ({ correlation, categoryGroups, onSave, onClose }) => {
  const isEdit = correlation != null;
  const [description, setDescription] = useState(correlation?.description ?? '');
  const [accountingPlanErpId, setAccountingPlanErpId] = useState<number | null>(correlation?.accountingPlanErpId ?? null);
  const [movement, setMovement] = useState<number | null>(correlation?.movement ?? null);
  const [autoCreate, setAutoCreate] = useState<boolean>(correlation?.autoCreate ?? false);
  const [appliesTo, setAppliesTo] = useState<number>(correlation?.appliesTo ?? APPLIES_BOTH);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // O lançamento automático só existe no fluxo do extrato: regra só-cartão não
  // pode carregar o flag.
  const isCardOnly = appliesTo === APPLIES_CARD;
  const effectiveAutoCreate = isCardOnly ? false : autoCreate;

  // Com sentido definido, o select só oferece categorias daquele movimento —
  // a mesma regra que o backend impõe no casamento (MatchImportCorrelation).
  const filteredGroups =
    movement === 0 || movement === 1 ? filterCategoryGroupsByMovement(categoryGroups, movement) : categoryGroups;

  const handleSubmit = async () => {
    setError(null);
    if (!description.trim()) {
      setError('Informe a descrição (texto do extrato ou da fatura)');
      return;
    }
    if (accountingPlanErpId == null) {
      setError('Selecione a categoria');
      return;
    }
    if (
      movement != null &&
      !filteredGroups.some((g) => g.options.some((o) => o.value === accountingPlanErpId))
    ) {
      setError('A categoria escolhida não é do sentido selecionado');
      return;
    }
    try {
      setSaving(true);
      await onSave(description.trim(), accountingPlanErpId, movement, effectiveAutoCreate, appliesTo);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar correlação');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? 'Editar Correlação' : 'Nova Correlação'}
      icon={<Link2 size={20} className="text-emerald-600" />}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtnCls}>
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving} className={primaryBtnCls}>
            {saving ? <RefreshCw size={14} className="animate-spin" /> : null}
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      <div>
        <label className={labelCls}>Descrição (texto do extrato ou da fatura) *</label>
        <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} autoFocus />
        <p className="text-xs text-slate-400 mt-1">
          Lançamentos importados cuja descrição contenha este texto serão classificados automaticamente.
        </p>
      </div>
      <div>
        <label className={labelCls}>Aplica-se a</label>
        <select
          className={selectCls}
          value={String(appliesTo)}
          onChange={(e) => setAppliesTo(Number(e.target.value))}
        >
          <option value={String(APPLIES_BOTH)}>Ambos</option>
          <option value={String(APPLIES_STATEMENT)}>Extrato bancário</option>
          <option value={String(APPLIES_CARD)}>Fatura de cartão de crédito</option>
        </select>
        <p className="text-xs text-slate-400 mt-1">
          Uma regra de fornecedor da fatura de cartão não deve casar num pagamento do extrato (e vice-versa).
        </p>
      </div>
      <div>
        <label className={labelCls}>Sentido</label>
        <select
          className={selectCls}
          value={movement == null ? '' : String(movement)}
          onChange={(e) => setMovement(e.target.value === '' ? null : Number(e.target.value))}
        >
          <option value="">Ambos</option>
          <option value="1">Entrada (crédito)</option>
          <option value="0">Saída (débito)</option>
        </select>
        <p className="text-xs text-slate-400 mt-1">
          Com sentido definido, a regra só casa com eventos daquele movimento — um débito nunca ganha categoria de receita.
        </p>
      </div>
      <div>
        <label className={labelCls}>Categoria *</label>
        <CategorySelect groups={filteredGroups} value={accountingPlanErpId} onChange={setAccountingPlanErpId} />
      </div>
      <label className={`flex items-start gap-2 text-sm ${isCardOnly ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700 cursor-pointer'}`}>
        <input
          type="checkbox"
          className="mt-0.5"
          checked={effectiveAutoCreate}
          disabled={isCardOnly}
          onChange={(e) => setAutoCreate(e.target.checked)}
        />
        <span>
          Lançar automaticamente no extrato Itaú
          <span className="block text-xs text-slate-400">
            {isCardOnly
              ? 'O lançamento automático só existe no fluxo do extrato — na fatura de cartão a regra apenas sugere a categoria.'
              : 'A sincronização diária cria o lançamento sozinha quando o evento casa. Sem esta opção, a regra apenas sugere a categoria na tela de conciliação.'}
          </span>
        </span>
      </label>
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
};

// ─── View Principal ─────────────────────────────────────────────

export const ImportCorrelationsView: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [categoryGroups, setCategoryGroups] = useState<CategoryOptionGroup[]>([]);

  const [correlations, setCorrelations] = useState<ImportCorrelation[]>([]);
  const [loading, setLoading] = useState(false);
  const [correlationModal, setCorrelationModal] = useState<{ correlation: ImportCorrelation | null } | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    companyService
      .getCompanies()
      .then((list) => {
        setCompanies(list);
        if (list.length > 0) setCompanyId((prev) => prev ?? list[0].company_id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar empresas'));
  }, []);

  // Plano de contas da empresa (para o select e os rótulos da tabela).
  useEffect(() => {
    if (companyId == null) return;
    financialService
      .getAccountingPlans(companyId)
      .then((tree) => setCategoryGroups(buildCategoryOptions(tree)))
      .catch(() => setCategoryGroups([]));
  }, [companyId]);

  // Rótulo da categoria resolvido pelos grupos já carregados (o backend só
  // devolve o ErpId).
  const categoryLabelFor = (erpId: number | null): string | null => {
    if (erpId == null) return null;
    for (const g of categoryGroups) {
      const opt = g.options.find((o) => o.value === erpId);
      if (opt) return opt.label;
    }
    return null;
  };

  const loadCorrelations = useCallback(async () => {
    if (companyId == null) return;
    setLoading(true);
    setError(null);
    try {
      const list = await financialService.getImportCorrelations(companyId);
      setCorrelations(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar correlações');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadCorrelations();
  }, [loadCorrelations]);

  const handleSaveCorrelation = async (
    description: string,
    accountingPlanErpId: number | null,
    movement: number | null,
    autoCreate: boolean,
    appliesTo: number,
  ) => {
    if (companyId == null) throw new Error('Selecione uma empresa');
    const payload = { companyId, description, accountingPlanErpId, movement, autoCreate, appliesTo };
    if (correlationModal?.correlation) {
      await financialService.updateImportCorrelation(correlationModal.correlation.transactionImportCorrelationId, payload);
      setNotice('Correlação atualizada com sucesso');
    } else {
      await financialService.createImportCorrelation(payload);
      setNotice('Correlação criada com sucesso');
    }
    await loadCorrelations();
  };

  const handleDeleteCorrelation = async (correlation: ImportCorrelation) => {
    if (!window.confirm(`Excluir a correlação "${correlation.description}"?`)) return;
    setError(null);
    try {
      await financialService.deleteImportCorrelation(correlation.transactionImportCorrelationId);
      setNotice('Correlação excluída com sucesso');
      await loadCorrelations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir correlação');
    }
  };

  return (
    <PermissionGuard permission="financial.import.execute" fallback={<PermissionFallback label="as correlações de importação" />}>
      <div className="space-y-6">
        {correlationModal && (
          <CorrelationModal
            correlation={correlationModal.correlation}
            categoryGroups={categoryGroups}
            onSave={handleSaveCorrelation}
            onClose={() => setCorrelationModal(null)}
          />
        )}

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Correlações de Importação</h1>
          <p className="text-sm text-slate-500 mt-1">
            Regras de classificação automática — valem para a conciliação do extrato bancário e para o upload da fatura de cartão
          </p>
        </div>

        {notice && <SuccessBox message={notice} onDismiss={() => setNotice(null)} />}
        {error && <ErrorBox message={error} />}

        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <label className={labelCls}>Empresa</label>
            <select
              className={selectCls + ' w-64'}
              value={companyId ?? ''}
              onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : null)}
            >
              {companies.map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {formatCnpj(c.document)} — {c.name}
                </option>
              ))}
            </select>
          </div>
          <button className={primaryBtnCls} onClick={() => setCorrelationModal({ correlation: null })} disabled={companyId == null}>
            <Plus size={16} /> Nova Correlação
          </button>
        </div>

        {loading && <LoadingSpinner />}

        {!loading && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição (texto importado)</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Categoria</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Aplica-se a</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Sentido</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Auto</th>
                    <th className="text-right py-2 px-3 font-semibold text-slate-600 w-24">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {correlations.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-10 text-center text-slate-400">
                        Nenhuma correlação cadastrada
                      </td>
                    </tr>
                  )}
                  {correlations.map((correlation) => (
                    <tr key={correlation.transactionImportCorrelationId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 text-slate-700">{correlation.description}</td>
                      <td className="py-2 px-3 text-slate-500">
                        {correlation.categoryLabel ?? categoryLabelFor(correlation.accountingPlanErpId) ?? '—'}
                      </td>
                      <td className="py-2 px-3 text-slate-500">{appliesToLabel(correlation.appliesTo)}</td>
                      <td className="py-2 px-3 text-slate-500">{movementLabel(correlation.movement)}</td>
                      <td className="py-2 px-3">
                        {correlation.appliesTo === APPLIES_CARD ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : correlation.autoCreate ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Automático
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">Só sugere</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            className="p-1.5 text-slate-400 hover:text-emerald-600 rounded"
                            title="Editar"
                            onClick={() => setCorrelationModal({ correlation })}
                          >
                            <Edit2 size={15} />
                          </button>
                          <button
                            className="p-1.5 text-slate-400 hover:text-red-500 rounded"
                            title="Excluir"
                            onClick={() => handleDeleteCorrelation(correlation)}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};

export default ImportCorrelationsView;
